/**
 * Behavior Registry
 *
 * Central registry mapping @type strings to behavior constructors.
 * This module is intentionally a leaf module with no behavior imports —
 * each behavior self-registers via registerBehavior() in its own module.
 */

import type { TraceBehavior } from './tracebehavior';
import type { BehaviorData } from '../scenedata/behaviordata';
import type { SceneObject } from '../core/SceneObject';

/**
 * Extract the TData type from a TraceBehavior<TData> subclass.
 */
export type DataOf<T extends TraceBehavior> = T extends TraceBehavior<infer D> ? D : BehaviorData;

/**
 * Constructor type for a TraceBehavior subclass with a static `behaviorType`.
 * When used with a specific T, overrides are typed as Partial<Omit<DataOf<T>, '@type'>>.
 */
export type BehaviorConstructor<T extends TraceBehavior = TraceBehavior> = {
    new (sceneObject: SceneObject, data: DataOf<T>): T;
    readonly behaviorType: string;
};

/** Registry: @type string → constructor */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- heterogeneous map requires widened type
const BEHAVIOR_REGISTRY = new Map<string, BehaviorConstructor<any>>(); // oxlint-ignore

/**
 * Look up a behavior constructor by @type string.
 * Returns undefined for unregistered types (e.g., unknown types).
 */
export function getBehaviorConstructor(type: string): BehaviorConstructor | undefined {
    return BEHAVIOR_REGISTRY.get(type);
}

/**
 * Register a behavior constructor.
 * Called by each behavior module to self-register.
 */
export function registerBehavior<T extends TraceBehavior>(ctor: BehaviorConstructor<T>): void {
    BEHAVIOR_REGISTRY.set(ctor.behaviorType, ctor);
}
